import React, { useCallback } from 'react';

import { LinearProgress } from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import RepeatIcon from '@material-ui/icons/Repeat';
import EmployeeOwnerAvatar from 'components/EmployeeOwnerAvatar';
import GoalProgressBar from 'components/GoalProgressBar';
import NodesView from 'components/NodesView';
import TextParseComponent from 'components/feed/TextParseComponent';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { withRouter } from 'react-router-dom';
import ParentPostLabel from 'screens/common/parentPostLabel';
import { StyledDateField } from 'screens/common/sidepanel/DetailSection/DateField';
import { CheckRadio } from 'screens/home/tasksList/styles';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledTypography, StyledIconButton, StyledTooltip, FlexContainer } from 'styles/common';

import { SEARCH_TAG_TYPES, REPEATING_TASK_TEXT, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { getColorFromKey, getTagDetailRoute } from 'constants/helper';
import { AddPeerIcon, HierarchyTwoChildIcon, UncheckedCircle } from 'constants/icons';

import { getCommaSeparatedTeamNames, getTags, sortToIdsOwnersFirstAscendingName } from 'services/helpers/task';
import { showTaskDetails } from 'services/taskDetails';

import { StyledTableRow, StyledTableCell, StyledTaskDetailsIcon } from './styles';

function TaskRowView({
  history,
  task,
  taskId,
  enableEditing,
  toggleCompletionStatus,
  removeTaskInReducer,
  updateTaskInReducer,
  tagType,
  type,
  employee,
  setDefaultCreateTask,
  setIsOpenCreateGoalPanel,
  isMSTeamsView = false,
}) {
  const dispatch = useDispatch();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const toIds = task?.toIds || [];

  const firstTag = task.getFirstTag();
  function onTagClick() {
    if (firstTag) {
      history.push(
        getTagDetailRoute({
          tagId: firstTag.uuid,
          tagName: firstTag.name.replace('#', ''),
          tagType: firstTag.tagType,
        })
      );
    }
  }

  function toggleCheckbox(event) {
    const isChecked = Boolean(task.completionDate);
    // const isChecked = event.target.checked;
    toggleCompletionStatus({ task, taskId, isChecked });
  }

  const onDelete = useCallback(
    (taskId) => {
      removeTaskInReducer({ taskId });
    },
    [removeTaskInReducer]
  );

  const showDetails = (taskId) =>
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete,
        onUpdate: (task) => {
          updateTaskInReducer && updateTaskInReducer({ newTask: task });
        },
        employee: loggedInEmployee,
        type: task.type,
      })
    );

  // inside a row, if image is clicked, then it takes the user to profile page
  // so making sure that when image is clicked we don't open goal panel
  const onGoalRowClick = (e) => {
    const { target } = e;
    const isANonClickableTarget = ['IMG', 'P'].includes(target.nodeName);
    if (isANonClickableTarget) return;
    showDetails(taskId);
  };

  const onEditableFieldClick = (e) => {
    e.stopPropagation();
    enableEditing(taskId);
  };

  const handleDuplicateGoalCreation = (e) => {
    e.stopPropagation();
    setDefaultCreateTask({ ...task });
    setIsOpenCreateGoalPanel(true);
  };

  function renderGoalsView() {
    const employees = sortToIdsOwnersFirstAscendingName(toIds);
    return (
      <GoalRowView onClick={onGoalRowClick} completed={task.completionDate}>
        {/* Goal List */}
        <StyledTableCell border width="35%" paddingLeft="1.5rem">
          <NameTypography onClick={onEditableFieldClick} display="inline" wordBreak variant="body2">
            <TextParseComponent text={task.text} res={task.macros} />
          </NameTypography>
        </StyledTableCell>
        {/* Objective */}
        <StyledTableCell border paddingLeft="1rem" width="10%">
          {task.subObjective && (
            <ObjectiveTypography onClick={onEditableFieldClick} color="textSecondary" variant="body2" truncate>
              {task.subObjective.name}
            </ObjectiveTypography>
          )}
        </StyledTableCell>
        <StyledTableCell border width="22%" paddingLeft="1rem" paddingRight="10px" align="center">
          <NodesViewContainer onClick={onEditableFieldClick}>
            <NodesView
              employees={employees}
              maxNodes={3}
              renderAvatar={(props) => (
                <EmployeeOwnerAvatar
                  {...props}
                  onClick={onEditableFieldClick}
                  size="fixed32px"
                  leftGutter={-0.5}
                  border="2px solid white "
                />
              )}
              renderEmptyView={(props) => (
                <AddPeerIcon
                  {...props}
                  color="white"
                  circular
                  size="fixed28px"
                  style={{ marginLeft: employees.length ? '-6px' : '0px' }}
                />
              )}
            />
          </NodesViewContainer>
        </StyledTableCell>
        <StyledTableCell border paddingLeft="1.65rem" width="29%">
          <GoalProgressBar goal={task} />

          <StyledTypography variant="body2" display="inline">
            {`${Number(task.getGoalProgress(0) || 0)}%`}
          </StyledTypography>
        </StyledTableCell>
        <StyledTableCell border width="9%">
          <FlexContainer>
            {!isMSTeamsView && (
              <StyledTooltip title="Create new goal in sub-objective">
                <CustomStyledIconButton
                  circular
                  color="primary"
                  backgroundColor="white"
                  borderColor="primary"
                  onClick={handleDuplicateGoalCreation}
                >
                  <StyledHeirarchyIcon />
                </CustomStyledIconButton>
              </StyledTooltip>
            )}

            <StyledTaskDetailsIcon
              style={{ marginLeft: '.75rem' }}
              taskId={task.id}
              showDetails={showDetails}
              color="primary"
            />
          </FlexContainer>
        </StyledTableCell>
      </GoalRowView>
    );
  }

  function renderOwnerAndCollaboratorColumn() {
    const employees = sortToIdsOwnersFirstAscendingName(toIds);
    return (
      <NodesViewContainer onClick={onEditableFieldClick}>
        <NodesView
          employees={employees}
          maxNodes={3}
          leftGutter={-0.75}
          renderAvatar={(props) => (
            <EmployeeOwnerAvatar
              {...props}
              onClick={onEditableFieldClick}
              size="fixed32px"
              border="2px solid white "
              render
            />
          )}
          renderEmptyView={(props) => (
            <AddPeerIcon
              {...props}
              color="white"
              circular
              size="fixed28px"
              style={{ marginLeft: employees.length ? '-6px' : '0px', cursor: 'pointer' }}
            />
          )}
        />
      </NodesViewContainer>
    );
  }

  const isGoalView = type === TASK_TYPES_2.goal;

  const coloredDueDate = task.coloredDueDate || {};

  return isGoalView ? (
    renderGoalsView()
  ) : (
    <GoalRowView isEditing={task.isEditing} completed={task.completionDate}>
      <StyledTableCell border>
        {!task.isUnassignedTask() && (
          <CheckRadio
            hoverColor="textSecondary"
            onClick={task.isUpdating ? null : toggleCheckbox}
            // onChange={task.isUpdating ? null : toggleCheckbox}
            disableRipple
            checked={Boolean(task.completionDate)}
            icon={<UncheckedCircle size="header" color="textSecondary" />}
            size="h6"
            checkedIcon={<UncheckedCircle size="header" color="green" />}
          />
        )}
      </StyledTableCell>

      <StyledTableCell paddingLeft="0rem" paddingRight="0.5rem" width="33%" align="center" border>
        <NameTypography
          display="inline"
          wordBreak
          variant="body2"
          onClick={() => enableEditing(taskId)}
          completed={task.isComplete()}
        >
          <TextParseComponent text={task.text} res={task.macros} />
        </NameTypography>
      </StyledTableCell>
      {/* <StyledTableCell border={true} width="27%" paddingLeft="0"></StyledTableCell> */}
      <StyledTableCell border width="12%" paddingLeft="0">
        <div style={{ maxWidth: '175px' }}>
          <ParentPostLabel parentPosts={task.parentPosts} />
        </div>
      </StyledTableCell>
      <StyledTableCell border width="12%" paddingLeft="0">
        {tagType === SEARCH_TAG_TYPES.team &&
          (getTags(task).length > 0 ? (
            <StyledTypography variant="body2" onClick={onTagClick}>
              {getTags(task)
                .map((tag) => `#${tag.name}`)
                .join(', ')}
            </StyledTypography>
          ) : (
            <StyledTypography variant="body2"> - </StyledTypography>
          ))}
        {
          // show teams if its the tag page
          tagType === SEARCH_TAG_TYPES.tag && (
            <StyledTypography variant="body2">{getCommaSeparatedTeamNames(task)}</StyledTypography>
          )
        }
      </StyledTableCell>
      <StyledTableCell border width="16%" paddingLeft="0" paddingRight="0.5rem" align="center">
        {renderOwnerAndCollaboratorColumn()}
      </StyledTableCell>

      <StyledTableCell border paddingLeft="1.65rem" width="10%">
        {task.isRecurrent() && (
          <StyledTooltip title={REPEATING_TASK_TEXT}>
            <StatsIconContainer absolute>
              <RepeatIcon />
            </StatsIconContainer>
          </StyledTooltip>
        )}
        {coloredDueDate.timeStamp && (
          <StyledDateField borderColor={coloredDueDate.color} tooltip={coloredDueDate.reason}>
            <StyledDateField.DatePresentDisplay date={new Date(coloredDueDate.timeStamp)} />
          </StyledDateField>
        )}
      </StyledTableCell>

      <StyledTableCell border paddingLeft="0" width="8%">
        {task.discussionCommentsCount > 0 && (
          <StatsIconContainer as="span">
            <ChatBubbleOutlineIcon />
            <span>{task.discussionCommentsCount}</span>
          </StatsIconContainer>
        )}
        {task.subTaskStats && task.subTaskStats.total > 0 && (
          <StatsIconContainer style={{ marginLeft: '4px' }} width="55px" as="span">
            <PlaylistAddCheckIcon style={{ fontSize: '18px' }} />
            <span>
              {task.subTaskStats.completed}/{task.subTaskStats.total}
            </span>
          </StatsIconContainer>
        )}
      </StyledTableCell>
      <StyledTableCell border>
        <StyledTaskDetailsIcon taskId={task.id} showDetails={showDetails} color="primary" />
      </StyledTableCell>
    </GoalRowView>
  );
}

export default withRouter(TaskRowView);

const StatsIconContainer = styled.div`
  ${(props) =>
    props.absolute &&
    css`
      position: absolute;
      top: 57%;
      left: 4%;
      transform: translateY(-49%);
    `};
  color: #999;
  width: ${(props) => (props.width ? props.width : 'auto')};
  &:first-child {
  }
  svg {
    font-size: 16px;
    margin-bottom: 1px;
  }
  span {
    font-size: 0.75rem;
    vertical-align: super;
    display: inline-block;
    margin: 6px 0 0 3px;
  }
`;

const StyledLinearProgress = styled(LinearProgress)`
  .MuiLinearProgress-bar1Determinate {
    ${(props) =>
      props.barColor &&
      css`
        background: ${(props) => getColorFromKey(props.barColor)};
      `}
  }
  &.MuiLinearProgress-root {
    display: inline-block;
    width: 75%;
    margin-right: 0.5rem;
    height: 0.5rem;
    border-radius: 4px;
  }
  &.MuiLinearProgress-colorPrimary {
    background: #eaeaea;
  }
  // this style is not working currently but adding it just in case for extra safety
  .MuiLinearProgress-bar2Determinate {
    background: #eaeaea;
  }
`;

const NodesViewContainer = styled.div`
  display: inline-block;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 0.1rem;
  // margin-left: -0.5rem;
`;
const ObjectiveTypography = styled(StyledTypography)`
  padding-right: 0.5rem;
`;

const NameTypography = styled(StyledTypography)`
  display: inline-block;
  padding: 0.5rem;
  margin-left: -0.5rem;
  border: 1px solid transparent;
  border-radius: 4px;
  text-decoration: ${({ completed }) => (completed ? 'line-through' : 'none')};
`;

const EditableRowHoverCSS = css`
  &:hover {
    background: ${(props) => props.theme.palette.background.lightBlue};
    ${NameTypography} {
      border: 1px solid #eee;
      min-width: 60%;
      cursor: text;
      background: #ffffffdb;
    }
    ${ObjectiveTypography} {
      border: 1px solid #eee;
      border-radius: 4px;
      padding: 0.5rem calc(0.5rem - 1px);
      cursor: text;
      margin-left: -0.5rem;
      background: #ffffffdb;
    }
    // ${NodesViewContainer} {
    //   border: 1px solid #eee;
    //   min-width: 70%;
    //   background: #ffffffdb;
    //   cursor: text;
    //   display: inline-block;
    // }
  }
`;

const CustomStyledIconButton = styled(StyledIconButton)`
  opacity: 0;
  &.MuiButtonBase-root {
    width: ${(props) => (props.size === 'big' ? '28px' : '24px')};
    height: ${(props) => (props.size === 'big' ? '28px' : '24px')};
  }
`;

const StyledHeirarchyIcon = styled(HierarchyTwoChildIcon)`
  width: 12px;
  height: 12px;
`;

const GoalRowView = styled(StyledTableRow)`
  td {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
  &:hover {
    ${CustomStyledIconButton} {
      opacity: 100;
    }
  }
  ${EditableRowHoverCSS};
`;
